/**
 * @utilities/sentry
 * Utilities for Sentry to report errors.
 */

import * as Sentry from '@sentry/browser'

interface ISentryOptions {
  /**
   * If true, then the full Redux state will be logged (can't be larger than 200kb)
   */
  logState?: boolean
}

/**
 * sentryMiddleware
 * Redux middleware to log Redux action types as breadcrumbs
 * along with the last action on exceptions.
 *
 * Assumes Sentry is already initialized (it is in the base_global app).
 *
 * State can also be logged with by setting 'logState' to true.
 */
export function sentryMiddleware(options?: ISentryOptions) {
  return (store: any) => {
    return (next: any) => {
      return (action: any) => {
        // Log a breadcrumb for each action type dispatched
        Sentry.addBreadcrumb({
          category: 'redux',
          level: Sentry.Severity.Debug,
          message: action.type
        })

        // If there's an error on the next action, capture the exception
        // with context for the action object and full state
        try {
          return next(action)
        } catch (err) {
          Sentry.configureScope((scope) => {
            // Log the last action
            scope.setExtra('action', action)

            // Optionally log full Redux state
            if (options && options.logState === true) {
              scope.setExtra('state', store.getState())
            }
          })
          Sentry.captureException(err)
          // Re-throw the error (for app code to handle)
          throw err
        }
      }
    }
  }
}
